import * as React from 'react';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, Link } from 'react-router-dom';
import {
    Button,
    TextField,
    Grid,
    Box,
    Container,
    CssBaseline,
    Snackbar,
    Alert,
    CircularProgress,
    Tooltip,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import { validateEmail } from '../common/helpers/validation';
import { signUp as signUpAction, clearAuthError } from '../redux/actions/authActions';

const useStyles = makeStyles({
    logo: {
        maxWidth: "120px",
        color: '#0173BC',
    },
    loginBox: {
        padding: "1.5em",
        boxShadow: "0em .35em .5em #ccc",
        border: "1px solid #ddd",
    },
    link: {
        fontSize: ".85em",
    },
    loadingButton: {
        backgroundColor: '#ccc !important',
        color: '#666 !important',
        cursor: 'not-allowed !important',
        '&:hover': {
            backgroundColor: '#ccc !important',
        }
    }
});

const SignUp = () => {
    const navigate = useNavigate();
    const classes = useStyles();
    const dispatch = useDispatch();    
    // Fixed: Changed from SIGN_IN to SIGN_UP to match the actual action
    const loading = useSelector((state) => state.loading.SIGN_UP || state.loading.SIGN_IN);
    const [snackbarOpen, setSnackbarOpen] = React.useState(false);
    const [snackbarMessage, setSnackbarMessage] = React.useState('');
    const [snackbarSeverity, setSnackbarSeverity] = React.useState('success');
    const auth = useSelector((state) => state.auth);

    const [formData, setFormData] = React.useState({
        firstName: '',
        lastName: '',
        email: '',
        password: '',
        customer: '',
    });
    const [touchedFields, setTouchedFields] = React.useState({
        firstName: false,
        lastName: false,
        email: false,
        password: false,
        customer: false,
    });

    const [formErrors, setFormErrors] = React.useState({
        firstName: '',
        lastName: '',
        email: '',
        password: '',
        customer: '',
    });

    // Check if Sign Up button should be enabled - all fields must be filled and valid
    const isSignUpEnabled = 
        formData.firstName.trim() !== '' && 
        formData.lastName.trim() !== '' && 
        formData.email.trim() !== '' && 
        formData.password.trim() !== '' && 
        formData.customer.trim() !== '' &&
        !formErrors.firstName &&
        !formErrors.lastName &&
        !formErrors.email &&
        !formErrors.password &&
        !formErrors.customer &&
        !loading;

    useEffect(() => {
        if (auth.error && auth.error.includes("User already exists")) {
            setFormErrors(prev => ({
                ...prev,
                email: auth.error
            }));
        }
    }, [auth.error]);

    const validateField = (value, field) => {
        if (value.trim() === '') {
            return `${field} is required.`;
        }
        if (value.trim().length < 3) {
            return `${field} must be at least 3 characters long.`;
        }
        return '';
    };

    const validateEmailField = (email) => {
        if (email.trim() === '') {
            return 'Email is required.';
        }
        return validateEmail(email);
    };

    const validatePasswordField = (password) => {
        if (password.trim() === '') {
            return 'Password is required.';
        }
        
        const hasUpperCase = /[A-Z]/.test(password);
        const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);
        const hasNumericChar = /[0-9]/.test(password);

        if (password.length < 8) {
            return 'Password must be at least 8 characters long.';
        } else if (!hasUpperCase) {
            return 'Password must contain at least one uppercase letter.';
        } else if (!hasSpecialChar) {
            return 'Password must contain at least one special character.';
        } else if (!hasNumericChar) {
            return 'Password must contain at least one numeric character.';
        }
        
        return '';
    };

    const validateSingleField = (fieldName, value) => {
        let error = '';
        
        switch (fieldName) {
            case 'firstName':
                error = validateField(value, 'First Name');
                break;
            case 'lastName':
                error = validateField(value, 'Last Name');
                break;
            case 'email':
                error = validateEmailField(value);
                break;
            case 'customer':
                error = validateField(value, 'Customer');
                break;
            case 'password':
                error = validatePasswordField(value);
                break;
            default:
                break;
        }
        
        return error;
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        
        setFormData((prevData) => ({
            ...prevData,
            [name]: value,
        }));

        // Validate field in real-time if it has been touched
        if (touchedFields[name]) {
            const error = validateSingleField(name, value);
            setFormErrors(prev => ({
                ...prev,
                [name]: error
            }));
        }

        // Clear the email error when the user starts typing in the email field
        if (name === 'email' && formErrors.email) {
            setFormErrors(prev => ({
                ...prev,
                email: ''
            }));
        }
    };

    const handleBlur = (field) => {
        setTouchedFields((prevTouched) => ({
            ...prevTouched,
            [field]: true,
        }));

        const value = formData[field];
        const error = validateSingleField(field, value);
        
        setFormErrors(prev => ({
            ...prev,
            [field]: error
        }));
    };

    const handleSnackbarClose = () => {
        setSnackbarOpen(false);
    };

    const handleSnackbarOpen = (message, severity) => {
        setSnackbarMessage(message);
        setSnackbarSeverity(severity);
        setSnackbarOpen(true);
    };

    const validateAllFields = () => {
        const errors = {
            firstName: validateField(formData.firstName, 'First Name'),
            lastName: validateField(formData.lastName, 'Last Name'),
            email: validateEmailField(formData.email),
            customer: validateField(formData.customer, 'Customer'),
            password: validatePasswordField(formData.password),
        };

        setFormErrors(errors);

        // Mark all fields as touched
        setTouchedFields({
            firstName: true,
            lastName: true,
            email: true,
            password: true,
            customer: true,
        });

        // Return true if no errors
        return !Object.values(errors).some(error => error !== '');
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
    
        // Clear previous auth errors
        dispatch(clearAuthError());
    
        // Validate all fields
        const isValid = validateAllFields();
    
        if (!isValid) {
            return;
        }
    
        try {
            const formDataParameters = {
                firstName: formData.firstName,
                lastName: formData.lastName,
                email: formData.email,
                password: formData.password,
                customerName: formData.customer,
            };
    
            await dispatch(signUpAction(formDataParameters));
    
            if (auth.token) {
                navigate('/login');
            }
        } catch (error) {
            const responseError = error?.errors?.[0]?.errorMessage || 'An unknown error occurred.';
            if (responseError.includes("User already exists")) {
                setFormErrors(prev => ({
                    ...prev,
                    email: responseError
                }));
            } else {
                handleSnackbarOpen(responseError || 'Something went wrong. Please try again.', 'error');
            }
        }
    };

    return (
        <Container component="main" maxWidth="xs">
            <CssBaseline />
            <Box
                sx={{
                    marginTop: 8,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                }}
            >
                <Box>
                    <h1 className={classes.logo}>BizAiTek</h1>
                </Box>
                <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }} className={classes.loginBox} noValidate>
                    <Box sx={{ marginBottom: '10px' }}>
                        <label>First Name</label>
                        <TextField
                            fullWidth
                            id="firstName"
                            name="firstName"
                            autoComplete="firstName"
                            value={formData.firstName}
                            onChange={handleInputChange}
                            onBlur={() => handleBlur('firstName')}
                            error={touchedFields.firstName && !!formErrors.firstName}
                            helperText={touchedFields.firstName ? formErrors.firstName : ''}
                            disabled={loading}
                            inputProps={{ 'aria-required': true }}
                            required
                        />
                    </Box>
                    <Box sx={{ marginBottom: '10px' }}>
                        <label>Last Name</label>
                        <TextField
                            fullWidth
                            id="lastName"
                            name="lastName"
                            autoComplete="lastName"
                            value={formData.lastName}
                            onChange={handleInputChange}
                            onBlur={() => handleBlur('lastName')}
                            error={touchedFields.lastName && !!formErrors.lastName}
                            helperText={touchedFields.lastName ? formErrors.lastName : ''}
                            disabled={loading}
                            inputProps={{ 'aria-required': true }}
                            required
                        />
                    </Box>
                    <Box sx={{ marginBottom: '10px' }}>
                        <label>Email Address</label>
                        <TextField
                            fullWidth
                            id="email"
                            name="email"
                            autoComplete="email"
                            value={formData.email}
                            onChange={handleInputChange}
                            onBlur={() => handleBlur('email')}
                            error={touchedFields.email && !!formErrors.email}
                            helperText={touchedFields.email ? formErrors.email : ''}
                            disabled={loading}
                            inputProps={{ 'aria-required': true }}
                            required
                        />
                    </Box>
                    <Box sx={{ marginBottom: '10px' }}>
                        <label>Password</label>
                        <Tooltip
                            title="Password must be at least 8 characters long and contain at least one uppercase letter, one numeric character, and one special character."
                            arrow
                        >
                            <TextField
                                fullWidth
                                name="password"
                                type="password"
                                id="password"
                                autoComplete="current-password"
                                value={formData.password}
                                onChange={handleInputChange}
                                onBlur={() => handleBlur('password')}
                                error={touchedFields.password && !!formErrors.password}
                                helperText={touchedFields.password ? formErrors.password : ''}
                                disabled={loading}
                                inputProps={{ 'aria-required': true }}
                                required
                            />
                        </Tooltip>
                    </Box>
                    <Box sx={{ marginBottom: '10px' }}>
                        <label>Customer</label>
                        <TextField
                            fullWidth
                            id="customer"
                            name="customer"
                            autoComplete="customer name"
                            value={formData.customer}
                            onChange={handleInputChange}
                            onBlur={() => handleBlur('customer')}
                            error={touchedFields.customer && !!formErrors.customer}
                            helperText={touchedFields.customer ? formErrors.customer : ''}
                            disabled={loading}
                            inputProps={{ 'aria-required': true }}
                            required
                        />
                    </Box>
                    <Button
                        type="submit"
                        fullWidth
                        variant="contained"
                        sx={{ 
                            mt: 3, 
                            mb: 2,
                            minHeight: '42px',
                            transition: 'all 0.3s ease',
                            backgroundColor: isSignUpEnabled ? '#FFCB3D' : '#bdbdbd !important',
                            color: isSignUpEnabled ? '#000000' : '#757575 !important',
                            boxShadow: isSignUpEnabled ? undefined : 'none',
                            '&:hover': {
                                backgroundColor: isSignUpEnabled ? '#e6b836' : '#bdbdbd !important',
                            },
                            '&:disabled': {
                                backgroundColor: '#bdbdbd !important',
                                color: '#757575 !important',
                            }
                        }}
                        disabled={!isSignUpEnabled}
                    >
                        {loading ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <CircularProgress size={20} sx={{ color: '#757575' }} />
                                <span>Signing Up...</span>
                            </Box>
                        ) : (
                            'Sign Up'
                        )}
                    </Button>
                    <Grid container>
                        <Grid item>
                            <Link 
                                className={classes.link} 
                                to="/login" 
                                variant="body2"
                                style={{ 
                                    pointerEvents: loading ? 'none' : 'auto',
                                    opacity: loading ? 0.6 : 1 
                                }}
                            >
                                {"Do you have an account? Sign In"}
                            </Link>
                        </Grid>
                    </Grid>
                </Box>
            </Box>
            <Snackbar
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center',
                }}
                open={snackbarOpen}
                autoHideDuration={6000}
                onClose={handleSnackbarClose}
            >
                <Alert onClose={handleSnackbarClose} severity={snackbarSeverity}>
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </Container>
    );
};

export default SignUp;